import { useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useParams, useSearchParams } from 'react-router-dom';
import { validateOneTap } from '@/api/bff';
import { ErrorScreen } from '@/components/ErrorScreen/ErrorScreen';
import { FullScreenLoader } from '@/components/LoadScreen/FullScreenLoader';
import { useAirKeyTrace } from '@/utils/airKeyTrace';
import { cn } from '@/utils/cn';
import { getErrorType } from '@/utils/errors';
import { AppRoot } from '@cof/omni-react';
import { OneTapResult } from './OneTapResult';

type OneTapStatus = 'loading' | 'success' | 'fail' | 'error';

// AirKey NFC messages are fixed-size (60 or 80 chars for v1/v2 cards, see
// src/templates/3DS/3DS.tsx's PUID parsing); this is a generous ceiling to
// reject obviously-malformed/oversized `ak` values before they're ever
// forwarded to the BFF.
const MAX_AIRKEY_MESSAGE_LENGTH = 4096;

/**
 * OneTap entry point for `/tap/:cardType?ak={airkeyMessage}`.
 *
 * This route is sessionless (no policyProcessId), so it cannot nest under
 * AirKeyWrapper — ConfigProvider/TransactionProvider both hard-require
 * intentOwner/workflow/policyProcessId from useParams and would error out.
 * Instead this component supplies its own minimal shell (ErrorBoundary +
 * AppRoot, mirroring AirKeyWrapper minus the providers) and runs a
 * self-contained spinner -> result state machine.
 */
export function OneTap() {
  const { cardType = '' } = useParams();
  const [searchParams] = useSearchParams();
  const rawAirkeyMessage = searchParams.get('ak');
  const airkeyMessage =
    rawAirkeyMessage && rawAirkeyMessage.length <= MAX_AIRKEY_MESSAGE_LENGTH
      ? rawAirkeyMessage
      : null;

  const [status, setStatus] = useState<OneTapStatus>('loading');
  const [redirectUrl, setRedirectUrl] = useState<string | undefined>();

  const sendTrace = useAirKeyTrace();

  useEffect(() => {
    if (!airkeyMessage) {
      setStatus('error');
      return;
    }

    let isMounted = true;

    (async () => {
      try {
        const { validationResult, redirectUrl: nextRedirectUrl } =
          await validateOneTap(cardType, airkeyMessage);

        if (!isMounted) return;

        setRedirectUrl(nextRedirectUrl);
        setStatus(validationResult === 'Success' ? 'success' : 'fail');
      } catch (error) {
        if (!isMounted) return;

        sendTrace({
          view: 'error',
          action: 'system_error',
          additionalData: { error_type: getErrorType(error) },
        });
        setStatus('error');
      }
    })();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [airkeyMessage, cardType]);

  const renderContent = () => {
    switch (status) {
      case 'loading':
        return <FullScreenLoader />;
      case 'success':
      case 'fail':
        return <OneTapResult variant={status} redirectUrl={redirectUrl} />;
      case 'error':
      default:
        return <ErrorScreen />;
    }
  };

  return (
    <ErrorBoundary FallbackComponent={ErrorScreen}>
      <AppRoot className={cn('bg-page')}>{renderContent()}</AppRoot>
    </ErrorBoundary>
  );
}
